import React, { createContext, useContext, useState, useEffect, PropsWithChildren } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile as updateFirebaseProfile
} from 'firebase/auth';
import { cmsAuth } from '../config/firebase';
import { CmsAuthContextType } from '../types';

const CmsAuthContext = createContext<CmsAuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  signIn: async () => ({ success: false }),
  signOut: async () => {},
  updateProfile: async () => {},
});

export const useCmsAuth = () => {
  const context = useContext(CmsAuthContext);
  if (!context) {
    throw new Error('useCmsAuth must be used within a CmsAuthProvider');
  }
  return context;
};

export const CmsAuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<{ uid: string; email: string; displayName?: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Listen to Firebase Auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(cmsAuth, async (firebaseUser) => {
      setIsLoading(true);
      try {
        if (firebaseUser) {
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email!,
            displayName: firebaseUser.displayName || undefined,
          });
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Sign in with email and password
  const signIn = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(cmsAuth, email, password);
      return { success: true };
    } catch (error: any) {
      console.error('Sign in error:', error);
      return {
        success: false,
        code: error.code,
        message: error.message
      };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      await firebaseSignOut(cmsAuth);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  // Update user profile
  const updateProfile = async (data: { displayName?: string }) => {
    try {
      if (cmsAuth.currentUser) {
        await updateFirebaseProfile(cmsAuth.currentUser, {
          displayName: data.displayName
        });
        setUser(prev => prev ? { ...prev, displayName: data.displayName } : prev);
      }
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  };

  const value: CmsAuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    signIn,
    signOut,
    updateProfile,
  };

  return (
    <CmsAuthContext.Provider value={value}>
      {children}
    </CmsAuthContext.Provider>
  );
};